import { Observable, catchError, of, tap, throwError } from 'rxjs';
import { LogHelper } from '../logging/log-helper';
import { TimeSpan } from '../model/date.helper';

export type CacheKey<T> = {
  expiration: number;
  data: T;
};

export type FetchCacheOptions<T> = {
  expirationMs?: number;
  errorHandling?: (error: unknown) => Observable<T>;
};

export type LazyCacheOptions = {
  ttl?: number;
  cleanupInterval?: number;
};

export class LazyCache<T> {
  private readonly cache = new Map<string, CacheKey<T>>();
  private readonly logger = new LogHelper('Cache');
  private readonly options: LazyCacheOptions;

  constructor(options?: LazyCacheOptions) {
    this.options = options ?? {
      ttl: TimeSpan.fromMinutes(1),
      cleanupInterval: TimeSpan.fromMinutes(1),
    };
  }

  public set(key: string, data: T, expiration: number = this.options.ttl): boolean {
    setTimeout(() => this.clearExpired(), expiration);
    return !!this.cache.set(key, { expiration: Date.now() + expiration, data });
  }

  public get(key: string): T | null {
    const existing = this.cache.get(key);
    if (!existing || this.isExpired(existing)) {
      this.cache.delete(key);
      return null;
    }

    return existing.data;
  }

  public has(key: string): boolean {
    return !!this.get(key);
  }

  public buildCacheKey(...params: string[]) {
    return params.join(':');
  }

  public clear() {
    this.cache.clear();
  }

  public clearExpired() {
    for (const [key, value] of this.cache.entries()) {
      if (this.isExpired(value)) {
        this.logger.debug('Cache expired for key', key);
        this.cache.delete(key);
      }
    }
  }

  public fetchWithCache(
    key: string,
    apiCall: Observable<T>,
    options: FetchCacheOptions<T> = { expirationMs: this.options.ttl },
  ): Observable<T> {
    const cache = this.get(key);
    if (cache) {
      return of(cache);
    }

    return apiCall.pipe(
      tap((data) => this.set(key, data, options?.expirationMs ?? this.options.ttl)),
      catchError((error) => (options?.errorHandling ? options.errorHandling(error) : throwError(() => error))),
    );
  }

  private isExpired(key: CacheKey<T>): boolean {
    return key.expiration <= Date.now();
  }
}
